import { getCurrentProgramOrSubprogram } from '../getCurrentProgramOrSubprogram';
import { PollResult } from '../../modules/dataProviders/types';
import { PlayUnifiedLiveEpgQuery } from '../../modules/dataProviders/__generated__/Epg';
import { findCurrentEpgEntryProgram } from '../../modules/dataProviders/Epg';
import type { PlaybackTypes } from '../../types';

function getPlaybackType({
  isStartOver,
  isLive,
}: {
  isStartOver: boolean;
  isLive: boolean;
}): PlaybackTypes {
  if (isStartOver) {
    return 'start-over';
  }

  if (isLive) {
    return 'live';
  }

  return 'vod';
}

const allowSeekingInSubPrograms = true;

export type SeekableMediaInput = {
  metadata: {
    duration: number;
    title: string | null;
    startTime?: number;
    isLive: boolean;
    isStartOver: boolean;
  };
};

export function buildSeekableInformationValue(
  media: SeekableMediaInput,
  programEndScteMarker: number | null,
  epg: PollResult | null,
  currentTime: number
): SeekInformationValue {
  const playbackType = getPlaybackType(media.metadata);
  let { duration } = media.metadata;

  switch (playbackType) {
    case 'vod': {
      return {
        seekable: true,
        type: 'vod',
        stop: duration,
        start: 0,
        stopType: 'actual',
        duration,
        hideProgress: false,
      };
    }
    case 'live': {
      const value: SeekInformationValue = {
        seekable: false,
        type: 'live',
        stop: duration ?? 0,
        start: 0,
        stopType: 'planned',
        epgTitle: media.metadata.title ?? '',
        epgDurationLabel: '',
        duration: duration ?? 0,
        hideProgress: true,
      };

      // If we have epg data, we will try to find the current epg entry
      const mainProgram = findCurrentEpgEntry(epg?.liveEpgResult ?? null, currentTime);
      const subProgram = getCurrentProgramOrSubprogram(epg?.liveEpgResult);

      if (mainProgram != null && mainProgram.start != null && mainProgram.stop != null) {
        const { start, stop } = mainProgram;
        duration = stop - start;
        value.seekable = Boolean(mainProgram.startOverAvailable && mainProgram.transmissionId);

        value.startOverAvailable = Boolean(
          allowSeekingInSubPrograms
            ? subProgram && subProgram.startOverAvailable && subProgram.transmissionId
            : mainProgram.startOverAvailable && mainProgram.transmissionId
        );
        value.isVodAvailable = Boolean(
          subProgram &&
            subProgram.vodAvailableStart &&
            subProgram.vodAvailableStop &&
            subProgram.vodAvailableStart / 1000 < currentTime &&
            currentTime < subProgram.vodAvailableStop / 1000 &&
            subProgram.entity?.guid
        );
        value.transmissionId = subProgram ? subProgram?.transmissionId : undefined;
        value.entityGuid = subProgram ? subProgram?.entity?.guid : undefined;
        value.duration = duration;
        value.start = start;
        value.stop = stop;
        value.stopType = 'planned';
        value.epgTitle = mainProgram.title ?? '';
        value.subProgramTitle = subProgram ? subProgram.title ?? '' : '';
        value.epgDurationLabel = `${stringTimeFromNumber(start)} - ${stringTimeFromNumber(stop)}`;
        value.subprogramStart = (subProgram ? subProgram?.start : undefined) ?? undefined;
        value.subprogramStop = (subProgram ? subProgram?.stop : undefined) ?? undefined;
        value.type = 'live';
        value.entityId = mainProgram.entityId;
        value.imageUrl = subProgram ? subProgram?.imageUrl : mainProgram.imageUrl;
        value.hideProgress = false;
      }

      return value;
    }
    case 'start-over': {
      const value: SeekInformationValue = {
        seekable: true,
        type: 'start-over',
        stop: duration ?? 0,
        start: 0,
        stopType: 'planned',
        epgTitle: media.metadata.title ?? '',
        epgDurationLabel: '',
        duration: duration ?? 0,
        hideProgress: false,
      };

      // If we have epg data, we will try to find the current epg entry
      const currentEntry = epg?.startOverEpgResult;

      if (currentEntry != null) {
        const start = currentEntry.epg.programStart ?? 0;
        const stop = programEndScteMarker
          ? start + programEndScteMarker
          : currentEntry.epg.programStop ?? 0;

        duration = stop - start;

        value.currentTimeAdjustment = media.metadata.startTime;
        value.duration = duration;
        value.start = start;
        value.stop = stop;
        value.stopType = programEndScteMarker ? 'actual' : 'planned';
        value.epgTitle = currentEntry.epg.title ?? '';
        value.epgDurationLabel = `${stringTimeFromNumber(
          currentEntry.epg.start!
        )} - ${stringTimeFromNumber(currentEntry.epg.stop!)}`;

        value.entityId = currentEntry.epg.entityId;
      }

      return value;
    }
    default:
      throw new Error(`Unknown playback type: ${playbackType}`);
  }
}
function findCurrentEpgEntry(epg: PlayUnifiedLiveEpgQuery | null, tick: number) {
  if (epg == null) {
    return null;
  }
  return findCurrentEpgEntryProgram(epg, tick);
}

/**
 * Use this method to manipulate a seekable.updated event to get the values to show if you seek by a certain amount
 * @param value The value of the seekable.updated event
 * @param adjustment The amount of ticks to adjust the seekable.updated event by
 */
export function getVirtualAdjustmentData<
  T extends {
    seekMax: number;
    type: PlaybackTypes;
    max: number;
    currentTime: number;
    seekable: boolean;
    start?: number;
    stop: number;
    seekMin?: number;
  }
>(value: T, adjustment: number): T {
  if (!value.seekable) {
    return value;
  }

  // We ensure that the currentTime is between the seekMin and seekMax
  const currentTime = Math.max(
    value.seekMin ?? 0,
    Math.min(value.seekMax, value.currentTime + adjustment)
  );

  return {
    ...value,
    currentTime,
    currentTimeLabel: getCurrentTimeLabel(
      value.type,
      value.type === 'live' ? (value?.start ?? 0) + currentTime : currentTime
    ),
    endLabel: getEndLabel({
      type: value.type,
      duration: value.max,
      currentTime,
      stop: value.type === 'live' ? value.stop : value.max,
    }),
  };
}

function getEndLabelFromSeekInfo(seekInfo: SeekInformationValue, playerCurrentTime: number) {
  switch (seekInfo.type) {
    case 'live':
      return getEndLabel({ type: seekInfo.type, stop: seekInfo.stop });
    case 'start-over':
    case 'vod':
      return getEndLabel({
        type: seekInfo.type,
        duration: seekInfo.duration,
        currentTime: playerCurrentTime,
      });
    default:
      throw new Error('Unknown PlaybackType');
  }
}

export type SeekState = Exclude<ReturnType<typeof calculateSeekState>, null>;

export function calculateSeekState(
  seekInfo: SeekInformationValue | null,
  playerCurrentTime: number,
  liveEdge: number,
  virtualSeek?: number,
  duration?: number
) {
  if (seekInfo == null) return null;

  if (seekInfo.type === 'vod' && duration) {
    seekInfo.duration = duration;
  }
  let currentTime = playerCurrentTime;

  // When we are in live the currentTime in the player is the current clock time like 12:25
  // - so we will remove the start time to get the relative currentTime in the program
  if (seekInfo.type === 'live') {
    currentTime = playerCurrentTime - seekInfo.start;
  }

  let max = seekInfo.duration;

  let seekMax = seekInfo.duration;
  let seekMin = 0;

  if (seekInfo.type === 'start-over') {
    // If we have the actual stop time we will use that, otherwise we will use the planned stop time or the live edge - whichever is the largest
    max =
      seekInfo.stopType === 'actual' ? seekInfo.duration : Math.max(seekInfo.duration, liveEdge);
    seekMax = seekInfo.stopType === 'actual' ? max : liveEdge;
  }
  if (seekInfo.type === 'live') {
    seekMax = currentTime;
    seekMin =
      seekInfo && seekInfo.start && seekInfo.subprogramStart
        ? seekInfo.subprogramStart - seekInfo.start
        : 0;
  }

  const currentTimeLabel = getCurrentTimeLabel(seekInfo.type, playerCurrentTime);
  const endLabel = getEndLabelFromSeekInfo(seekInfo, playerCurrentTime);

  const value = {
    ...seekInfo,
    seekMax,
    seekMin,
    liveMarker: liveEdge,
    max,
    currentTime,
    currentTimeLabel,
    endLabel,
  };

  if (virtualSeek != null) {
    return getVirtualAdjustmentData(value, virtualSeek - currentTime);
  }
  return value;
}

function getCurrentTimeLabel(type: PlaybackTypes, tick: number) {
  switch (type) {
    case 'start-over':
      return stringDurationFromNumber(tick);
    case 'live':
      return stringTimeFromNumber(tick);
    default:
      return stringDurationFromNumber(tick);
  }
}

type GetEndLabelArgs =
  | {
      type: 'vod';
      duration: number;
      currentTime: number;
    }
  | {
      type: 'live';
      stop: number;
    }
  | {
      type: 'start-over';
      duration: number;
      currentTime: number;
    };

function getEndLabel(args: GetEndLabelArgs) {
  switch (args.type) {
    case 'start-over': {
      const value = args.duration - args.currentTime;
      if (value < 0) {
        return 'Forlænget';
      }
      return stringDurationFromNumber(value, '-');
    }
    case 'live':
      return stringTimeFromNumber(args.stop);
    default: {
      const value = args.duration - args.currentTime;
      return stringDurationFromNumber(value, '-');
    }
  }
}

export function stringTimeFromNumber(value: number) {
  const date = new Date(value * 1000);

  // Formats the date in the format HH.mm without using format
  const hours = date.getHours();
  const minutes = date.getMinutes();
  return `${hours < 10 ? '0' : ''}${hours}.${minutes < 10 ? '0' : ''}${minutes}`;
}

/** Formats the value in the format mm.ss where the value is seconds
 * If the value is more than 1 hour, the format will be H:mm:ss
 * add a prefix if you want
 */
function stringDurationFromNumber(value: number, prefix?: string) {
  try {
    const date = new Date(0, 0, 0, 0, 0, value);

    // If the value is more than 1 hour, the format will be H:mm:ss
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();
    const minutesAndSeconds = `${minutes < 10 ? '0' : ''}${minutes}:${
      seconds < 10 ? '0' : ''
    }${seconds}`;
    const formatted = hours > 0 ? `${hours}:${minutesAndSeconds}` : minutesAndSeconds;

    return (prefix ?? '') + formatted;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Invalid value', value, e);
    return '?';
  }
}

export type SeekInformationValue = {
  currentTimeAdjustment?: number;
  start: number;
  /** The end time of the current media */
  stop: number;

  /** Specifies if the stop type is the actual stop time or the time that we just think that the program ends
   * For live this is always 'planned'
   * For vod this is always 'actual'
   * For start over this is 'planned' until we get the SCTE35 event that tells us that the program has ended
   */
  stopType: 'planned' | 'actual';

  duration: number;
  /** The current time of the playhead that should be rendered in the UI */
  // currentTimeLabel: string;

  /** The end time that should be rendered in the UI */
  // endLabel: string;

  /** If the current media is seekable */
  seekable: boolean;
  startOverAvailable?: boolean;

  /** The current time of the playhead */
  // currentTime: number;

  /** The title of the current epg entry */
  epgTitle?: string;
  subProgramTitle?: string;

  /** The 'duration-label' of the current EPG entry to show in the UI - for example: 13.00 - 14.00 */
  epgDurationLabel?: string;

  /** If the current media is a start over media */
  type: PlaybackTypes;
  entityId?: string | null;
  subprogramStart?: number;
  subprogramStop?: number;
  imageUrl?: string | null;
  isVodAvailable?: boolean;
  entityGuid?: string | null;
  transmissionId?: string | null;
  hideProgress: boolean;
};
